import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  listVrShootRentalPlan,
  addVrShootRentalPlan,
  updateVrShootRentalPlan,
  deleteVrShootRentalPlan,
} from "../../Redux/Actions/rentalPlanActions";
import { columnsVrShootRentalList } from "../../Global Functions/columnsAntd";
import TableComponent from "../../Global Functions/tableComponents";
import toast from "react-hot-toast";
import DateRangePopup from "../../Utils/dateFilterPopup";
import VrShootRentalPlanEditModal from "./addAndUpdateVrShootRentalPlan";
import { Button, Modal } from "antd";

const ListVrShootRentalPlan = () => {
  const dispatch = useDispatch();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [isDatePopupVisible, setIsDatePopupVisible] = useState(false);
  const [dateRange, setDateRange] = useState({ start: null, end: null });
  const [debouncedSearch, setDebouncedSearch] = useState(searchQuery);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
    showSizeChanger: true,
    pageSizeOptions: ["5", "10", "20", "50", "100"],
  });

  const { admin } = useSelector((state) => state.admin || {});
  const accessCode = admin?.data?.access_code || [];
  const canAdd = Array.isArray(accessCode) && accessCode.includes("add");
  const canEdit = Array.isArray(accessCode) && accessCode.includes("edit");

  const fetchDetails = useCallback(
    (showLoader = true) => {
      const payload = {
        page: pagination.current,
        limit: pagination.pageSize,
        filter: debouncedSearch,
        ...(dateRange.start && {
          start_date: dateRange.start.format("YYYY-MM-DD"),
        }),
        ...(dateRange.end && {
          end_date: dateRange.end.format("YYYY-MM-DD"),
        }),
      };

      return dispatch(listVrShootRentalPlan(payload, showLoader)).catch(() => {
        if (showLoader) {
          toast.error("Failed to fetch VR shoot rental plans list");
        }
        return Promise.reject();
      });
    },
    [
      dispatch,
      pagination.current,
      pagination.pageSize,
      debouncedSearch,
      dateRange,
    ]
  );

  const vrShootRentalPlanState = useSelector((state) => state.vrShootRentalPlan || {});
  const plans = useMemo(() => {
    return vrShootRentalPlanState.plans?.data || [];
  }, [vrShootRentalPlanState.plans]);
  const planTotal = useMemo(() => {
    return vrShootRentalPlanState.plans?.total || 0;
  }, [vrShootRentalPlanState.plans]);
  const { loading } = vrShootRentalPlanState;

  useEffect(() => {
    fetchDetails(true);
  }, [fetchDetails]);

  useEffect(() => {
    setPagination((prev) => ({ ...prev, total: planTotal }));
  }, [planTotal]);

  const handleDateRangeApply = (start, end) => {
    setDateRange({ start, end });
    setIsDatePopupVisible(false);
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 500);

    return () => clearTimeout(handler);
  }, [searchQuery]);

  const openEditModal = useCallback((plan = null) => {
    setSelectedPlan(plan);
    setIsModalOpen(true);
  }, []);

  const closeEditModal = () => {
    setSelectedPlan(null);
    setIsModalOpen(false);
  };

  const handleDelete = useCallback(
    (planId) => {
      Modal.confirm({
        title: "Confirm Delete",
        content: "Are you sure you want to delete this VR shoot rental plan? This action cannot be undone.",
        okText: "Yes, Delete",
        okType: "danger",
        onOk: async () => {
          try {
            await dispatch(deleteVrShootRentalPlan(planId));
            fetchDetails(false);
          } catch (error) {
            toast.error("Failed to delete plan");
          }
        },
      });
    },
    [dispatch, fetchDetails]
  );

  const handleSubmit = async (values) => {
    try {
      let result;

      if (selectedPlan?._id) {
        result = await dispatch(
          updateVrShootRentalPlan({
            id: selectedPlan._id,
            ...values,
          })
        );
      } else {
        result = await dispatch(
          addVrShootRentalPlan({
            ...values,
          })
        );
      }

      if (result) {
        fetchDetails(false);
        closeEditModal();
        return { success: true };
      }
    } catch (error) {
      toast.error("Failed to save plan");
      return { success: false };
    }
  };

  const handleTableChange = (pagination) => {
    setPagination(pagination);
    fetchDetails(false);
  };

  return (
    <>
      <div
        style={{ position: "absolute", marginTop: "9.6%", marginLeft: "89%" }}
      >
        {canAdd && (
          <Button
            style={{
              background: "#070C18",
              color: "white",
              padding: "18px",
              borderRadius: "20px",
              width: "130px",
            }}
            onClick={() => openEditModal(null)}
          >
            Add Plan
          </Button>
        )}
      </div>

      <TableComponent
        pagination={pagination}
        setState={setSearchQuery}
        dataSource={plans}
        onChange={handleTableChange}
        h3heading="VR Shoot Rental Plans List"
        onCalendarClick={() => setIsDatePopupVisible(true)}
        columns={columnsVrShootRentalList(
          pagination.current,
          pagination.pageSize,
          handleDelete,
          openEditModal,
          canEdit
        )}
        loading={loading}
      />

      <DateRangePopup
        visible={isDatePopupVisible}
        initialStart={dateRange.start}
        initialEnd={dateRange.end}
        onCancel={() => setIsDatePopupVisible(false)}
        onOk={handleDateRangeApply}
      />

      {isModalOpen && (
        <VrShootRentalPlanEditModal
          visible={isModalOpen}
          planData={selectedPlan}
          onSave={handleSubmit}
          onClose={closeEditModal}
        />
      )}
    </>
  );
};

export default ListVrShootRentalPlan;